import { Select } from "antd";
import { useEffect, useMemo } from "react";
import { ReactEditor, useSlate } from "slate-react";
import { Editor, Element, Transforms, Text, Range } from "slate";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasSelection } from "../utils/hasSelection";

interface FontSizeSelectProps {
  disabled?: boolean;
  value: string;
  onChange: (val: string) => void;
}

export const FontSizeSelect = ({ disabled, value, onChange }: FontSizeSelectProps) => {
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);
  const sizes = useMemo(
    () => [
      { value: "Default", label: t("Default") },
      { value: "10", label: "10" },
      { value: "14", label: "14" },
      { value: "18", label: "18" },
      { value: "22", label: "22" },
      { value: "26", label: "26" },
      { value: "30", label: "30" },
      { value: "34", label: "34" },
      { value: "38", label: "38" },
      { value: "42", label: "42" },
      { value: "46", label: "46" },
      { value: "50", label: "50" },
      { value: "54", label: "54" },
      { value: "56", label: "56" },
      { value: "60", label: "60" },
    ],
    []
  );
  const editor = useSlate();

  const handleChange = (value) => {
    const parsedValue = value === "Default" ? undefined : value;
    onChange(value);
    const newProperties = { fontSize: parsedValue };
    if (hasSelection(editor)) {
      if (hasSelection(editor)) {
        // Apply font family to the selected text
        Transforms.setNodes(editor, newProperties, { at: editor.selection, match: (n) => Text.isText(n), split: true });
      }
    }
    if (parsedValue) {
      Editor.addMark(editor, "fontSize", parsedValue);
    } else {
      Editor.removeMark(editor, "fontSize");
    }
    ReactEditor.focus(editor);
  };

  useEffect(() => {
    const selectedNodes = Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        match: (node) => {
          return Element.isElement(node) || Text.isText(node);
        },
      })
    );
    if (selectedNodes.length) {
      const nodeWithFontSize = selectedNodes.find(([node]) => !!node.fontSize);
      const selectedValue = nodeWithFontSize ? parseInt(nodeWithFontSize[0].fontSize as string) : "Default";
      if (Range.isCollapsed(editor.selection) && value !== selectedValue) {
        onChange(selectedValue.toString());
        if (selectedValue !== "Default") {
          Editor.addMark(editor, "fontSize", selectedValue);
        }
      }
    } else if (value !== "Default") {
      Editor.removeMark(editor, "fontSize");
      onChange("Default");
    }
  }, [editor?.selection]);

  return (
    <div className="d-flex" title={t("Font size")}>
      <FontAwesomeIcon fixedWidth size="lg" icon={["far", "text-size"]} className="mr-sm" />
      <Select
        disabled={disabled}
        value={value}
        size="small"
        style={{ width: 100 }}
        className="mr-1"
        onChange={handleChange}
        options={sizes}
      />
    </div>
  );
};
